import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Scope } from 'src/app/shared/enum/scope.enum';
import { IUser } from 'src/app/shared/interface/user.interface';
import { JWTService } from './jwt.service';
import { StorageService } from './storage.service';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root'
})
/** The AuthService class provides methods for managing user authentication and authorization. */
export class AuthService {

  constructor(
    private _storage: StorageService,
    private _userService: UserService,
    private _jwt: JWTService,
    private _router: Router) { }

  /**
   * This function sets the login status to true in the storage.
   * @param {boolean} status
   */
  public setLoginStatus(status: boolean) {
    this._storage.set('loginStatus', true)
  }

  /**
   * This function returns a boolean value indicating the login status of a user based on a value
   * stored in a storage object.
   * @returns The method `getLoginStatus()` is returning a boolean value. It is checking if the value
   * of the key 'loginStatus' in the storage is equal to the string 'true' (case-insensitive), and
   * returning `true` if it is, and `false` otherwise.
   */
  public getLoginStatus() {
    return (this._storage.get('loginStatus')?.toLowerCase() === 'true');
  }

  /**
   * The function logs in a user by setting their login status, setting their JWT token, and setting
   * their user information.
   * @param {string} token - A string representing the authentication token received from the server
   * after successful login.
   * @param {IUser} user - IUser is likely an interface or type that defines the structure of a user
   * object. It could include properties such as username, email, password, and other relevant
   * information about the user. The user parameter in the login function is has to be an object that
   * conforms to the IUser interface/type and contains the user
   */
  public login(token: string, user: IUser) {
    this._jwt.setToken(token);
    this._userService.setUser(user);
  }

  public twoFactorLogin(){
    this.setLoginStatus(true);
  }

  /**
   * The function logs out the user by removing their login status, user data, and token from storage
   * and navigating to the login page.
   */
  public logout(): void {
    this._storage.unset('loginStatus');
    this._storage.unset('user');
    this._storage.unset('token');
    this._router.navigate(['login']);
  }

  /**
   * This function checks if a user has the necessary scopes to access a certain feature.
   * @param {Scope[]} scopes - An array of Scope values that represent the required permissions for a
   * certain action or feature.
   * @returns a boolean value. It returns `true` if the user has the required scopes or if no scopes
   * are needed, and `false` otherwise.
   */
  hasRight(scopes : Scope[]) {
    const user = this._userService.getCurrentUser();
    const userScopes = JSON.parse(user ? user.role.scopes : '[]');
    if (userScopes && scopes) {
      // check for role restrictions
      let matchedScope = false;
      scopes.forEach(scope => {
        if (userScopes.find((el: any) => el === scope) || userScopes.find((el: any) => el === Scope.All) || scopes[0] === Scope.Default) {
          // if role is not authorized
          matchedScope = true;
        }
      });
      // if role is authorized or no role needed
      return matchedScope;
    }
    return false;
  }

  /**
    * This function checks if the curernt user is a company admin
    */
  isCompanyAdmin() {
    const user = this._userService.getCurrentUser();

    if(user.role.id == 4) {
      return true;
    }

    return false;
  }

  /**
    * This function checks if the curernt user is a company admin
    */
  isSuperAdmin() {
    const user = this._userService.getCurrentUser();

    if(user.role.id == 1) {
      return true;
    }

    return false;
  }

  /**
    * This function checks if the curernt user is an openhouse user
    */
  isOpenhouseUser() {
    const user = this._userService.getCurrentUser();

    if(user.role.id == 15) {
      return true;
    }

    return false;
  }

  /**
    * This function returns the current user's company id
    */
  getCompanyId() {
    const user = this._userService.getCurrentUser();

    if(user.company == null) {
      return 0;
    }
    return user.company.id;
  }

  /**
    * This function returns the current user's company name
    */
  getCompanyName() {
    const user = this._userService.getCurrentUser();

    if(user.company == null) {
      return "";
    }
    return user.company.name;
  }

  /**
    * This function checks if the current user is a company admin
    */
  getUserId() {
    const user = this._userService.getCurrentUser();

    return user.id;
  }

}
